export const STRATEGY_SECTION_IDS = [
    "keyMetrics",
    "strategyPosture",
    "marketInfluence",
    "propertyStructure",
    "priceGranularity",
    "rateRestrictions",
    "lastMinute",
    "seasonality",
    "dayAdjustments",
    "specialDates",
] as const;

export type StrategySectionId = (typeof STRATEGY_SECTION_IDS)[number];

export interface StrategySectionDefinition {
    id: StrategySectionId;
    fields: readonly string[];
    /**
     * Questions asked on this section's own screen, above the controls they
     * set. Each question belongs to exactly one section so the wizard step and
     * the editor prompt stay in agreement. Only Key Metrics still owns one —
     * the behavioural questionnaire is gone and the profile is picked outright.
     */
    questionIds: readonly string[];
    blocking: boolean;
}

export const STRATEGY_SECTION_REGISTRY: readonly StrategySectionDefinition[] = [
    {
        id: "keyMetrics",
        fields: ["roomKindHistoricalMetrics", "roomKindStrategyOverrides", "minPricePct", "maxPricePct", "minPriceFollowsSeasonality"],
        // The only question left anywhere. The step sets a floor, a base and a
        // ceiling per room type, so it asks for exactly those three prices
        // (q33). Skippable: the analysis prices each room type from its booking
        // history and the step lets the operator price one by hand, so the
        // stated prices are the fallback for a room type neither covered — plus
        // the band, when they are stated.
        questionIds: ["q33"],
        blocking: true,
    },
    {
        id: "strategyPosture",
        fields: ["optimizationGoal", "postureBias", "hybridChangeoverDays", "hybridPacePull", "occupancyScaleMax", "paceSensitivity", "occupancyDamping", "propertyOccupancyWeight", "propertyPaceWeight", "propertyDemandWeight", "propertyPickupWeight", "leadTimeCurve"],
        // No questions. The posture opens on Optivo Recommended (hybrid), its
        // reactivity is chosen directly from the gentle/moderate/aggressive
        // cards both surfaces show, and everything finer sits behind expert
        // mode — so there is nothing left for a question to decide that the
        // section does not already ask outright.
        questionIds: [],
        blocking: false,
    },
    {
        id: "marketInfluence",
        fields: [
            "marketInfluenceEnabled",
            "marketInfluenceStrength",
            "marketFollowEnabled",
            "marketFollowPositionPct",
            "weatherInfluenceEnabled",
            "weatherSensitivity",
        ],
        questionIds: [],
        blocking: false,
    },
    {
        id: "propertyStructure",
        fields: ["roomKindLeadTimes", "peakNightBookingWindowDays", "roomKindCapacities", "occupancyAdjustments"],
        questionIds: [],
        blocking: false,
    },
    {
        id: "priceGranularity",
        // The charm ending is part of the rounding decision, not a section of
        // its own — one control renders both, and moving the step re-picks the
        // ending in the same change.
        fields: ["priceGranularity", "priceCharmEnding"],
        questionIds: [],
        blocking: false,
    },
    {
        id: "rateRestrictions",
        // These are exactly the four controls `LosRestrictionsEditor` renders,
        // and `los-restrictions-editor.test.tsx` holds the two together. A
        // maximum stay was listed here for a long time with no control behind
        // it and no PMS field to send it to.
        fields: ["defaultMinLos", "weekendHolidayLosEnabled", "losReleaseWithinDays", "losWeakWeekReleaseEnabled"],
        questionIds: [],
        blocking: false,
    },
    {
        id: "lastMinute",
        fields: ["lastMinuteEnabled", "lastMinuteMinPrice", "lastMinuteMinPriceFollowsSeasonality", "lastMinutePacing", "lastMinuteAdjustmentSteps", "lastMinuteWindowHours", "lastMinuteDemandStart", "lastMinuteHoldDepth", "roomKindLastMinute", "gapFillingEnabled", "gapFillingMaxGapDays", "gapFillingAdjustmentPct"],
        questionIds: [],
        blocking: false,
    },
    {
        id: "seasonality",
        fields: ["monthlySeasonality", "highSeasonAdjustmentPct", "lowSeasonAdjustmentPct"],
        questionIds: [],
        blocking: false,
    },
    {
        id: "dayAdjustments",
        fields: [
            "mondayAdjustmentPct",
            "tuesdayAdjustmentPct",
            "wednesdayAdjustmentPct",
            "thursdayAdjustmentPct",
            "fridayAdjustmentPct",
            "saturdayAdjustmentPct",
            "sundayAdjustmentPct",
        ],
        questionIds: [],
        blocking: false,
    },
    {
        id: "specialDates",
        fields: ["holidayCountries", "customSpecialDates", "bridgeDayPremiumEnabled", "bridgeDayPremiumMultiplier", "shoulderDayEnabled", "shoulderDayAdjustmentPct"],
        questionIds: [],
        blocking: false,
    },
];

/**
 * The order both pricing surfaces present these sections in: the wizard walks
 * it as steps, the strategy editor stacks it as collapsibles. One constant so
 * the two cannot drift — `settings-tier.test.ts` holds each of them to it.
 *
 * The property-identity step (`propertyClassification`) is deliberately not
 * here. It captures what the property *is* — type, location, whether a comp set
 * is tracked — before anything can be priced, so it runs first in the wizard and
 * has no editor counterpart at all. Ordering it against the pricing sections
 * would be comparing a prerequisite with the work it unblocks.
 *
 * Strategy Posture leads what remains: the goal is what every number after it
 * is pursued for. The weekday spread sits ahead of Key Metrics because Key
 * Metrics sets the minimum and maximum a room type's price may reach, and the
 * spread is stated against the base price — a band chosen first is a band
 * chosen without knowing how far Friday and Saturday already push. It still
 * precedes the calendar exceptions, which override it rather than stack on it.
 */
export const CANONICAL_SECTION_ORDER: readonly StrategySectionId[] = [
    "strategyPosture",
    "marketInfluence",
    "dayAdjustments",
    "keyMetrics",
    "propertyStructure",
    "priceGranularity",
    "rateRestrictions",
    "lastMinute",
    "seasonality",
    "specialDates",
];

export function strategySection(id: StrategySectionId): StrategySectionDefinition {
    const section = STRATEGY_SECTION_REGISTRY.find((s) => s.id === id);
    if (!section) throw new Error(`Unknown strategy section: ${id}`);
    return section;
}

/** The section whose wizard gate asks a given question, if any. */
export function sectionForQuestion(questionId: string): StrategySectionId | undefined {
    return STRATEGY_SECTION_REGISTRY.find((s) => s.questionIds.includes(questionId))?.id;
}